import {
  getElementWithoutSafeZoneOffset,
  PAGE_HEIGHT,
  PAGE_WIDTH,
  PREVIOUS_PAGE_HEIGHT,
  PREVIOUS_PAGE_WIDTH,
  USE_SAFE_AREA,
  PAGE_WIDTH_PARAM,
  PAGE_HEIGHT_PARAM,
} from '@src/web-stories-wp/units';
import {
  AssetTypes,
  ASSET_TYPE,
  CARDS_ASSET_TYPE,
} from '../hooks/studioContext/CMSParams';
import type { Element } from '@src/web-stories-wp/story-editor/src/elements/types';
import type { Page } from '@src/web-stories-wp/story-editor/src/elements/types';
import elementIs from '@src/web-stories-wp/story-editor/src/elements/utils/elementIs';
import { syncTextListMarkerSize } from '@src/web-stories-wp/story-editor/src/elements/text/utils/listStyle';
import {
  getBoundedTypographyScale,
  scaleInlineFontSizes,
} from '@src/web-stories-wp/story-editor/src/elements/text/utils/mixedTypography';

function ensureActiveBackgroundElement(page: Page): Page {
  const hasActiveBackground = page.elements?.some(
    (element) => elementIs.backgroundable(element) && element.isBackground
  );

  if (hasActiveBackground || !page.defaultBackgroundElement) {
    return page;
  }

  return {
    ...page,
    elements: [
      page.defaultBackgroundElement,
      ...(page.elements || []).filter(
        (element) => element.id !== page.defaultBackgroundElement?.id
      ),
    ],
  };
}

/**
 * The canvas used to include safe zones, which affected the y position of elements.
 * This function converts older pages to remove the safe zones.
 */
function offsetPageWithSafeZone(page: Page): Page {
  if (page.withAbsolutePositioning || USE_SAFE_AREA) {
    // If we bring back safe areas, we might need to convert pages the other way round
    return page;
  }

  return {
    ...page,
    elements: page.elements?.map((element) =>
      getElementWithoutSafeZoneOffset(element)
    ),
    withAbsolutePositioning: true,
  };
}

/**
 * If the page width and height are coming from the URL, then we don't need to resize the page
 * (since the 412 value never came from a param, it was hardcoded as a default value in Studio)
 * the only exception is cards assets and templates, whose canvas size was coming from the page ratio params
 */
function shouldResizePage() {
  const searchParams = new URLSearchParams(window.location.search);

  const assetType = searchParams.get(ASSET_TYPE);
  const cardAssetType = searchParams.get(CARDS_ASSET_TYPE);

  if (
    assetType === AssetTypes.CardsAsset &&
    [
      AssetTypes.Story,
      AssetTypes.Vertical,
      AssetTypes.Clip,
      AssetTypes.Ad,
    ].includes(cardAssetType)
  ) {
    return true;
  }

  if (assetType === AssetTypes.Template) {
    const canvasWidth = searchParams.get(PAGE_WIDTH_PARAM);
    const canvasHeight = searchParams.get(PAGE_HEIGHT_PARAM);

    return (
      !searchParams.has(PAGE_WIDTH_PARAM) ||
      (canvasWidth === '1080' && canvasHeight === '1920')
    );
  }

  return !searchParams.has(PAGE_WIDTH_PARAM);
}

/**
 * The Studio canvas for Stories used to be smaller than the actual Story Page (412 pixels wide instead of 1080)
 * This function converts older pages to the new canvas size.
 */
function offsetPageWithWrongCanvasSize(page: Page): Page {
  if (page.isFullSize) {
    return page;
  }

  if (!shouldResizePage()) {
    return page;
  }

  return {
    ...page,
    elements: page.elements?.map((element) => getElementCanvasSize(element)),
    isFullSize: true,
  };
}

export function pageToCanvasPage(page: Page): Page {
  const pageWithActiveBackgroundElement = ensureActiveBackgroundElement(page);
  // We need to convert the page to the new canvas size before removing the safe zones
  // Since the safe zone calculation uses the canvas height
  const pageWithCorrectCanvasSize = offsetPageWithWrongCanvasSize(
    pageWithActiveBackgroundElement
  );

  return offsetPageWithSafeZone(pageWithCorrectCanvasSize);
}

function getElementCanvasSize(element: Element): Element {
  if (elementIs.backgroundable(element) && element.isBackground) {
    return element;
  }

  const widthMultiplier = PAGE_WIDTH / PREVIOUS_PAGE_WIDTH;
  const heightMultiplier = PAGE_HEIGHT / PREVIOUS_PAGE_HEIGHT;
  const typographyMultiplier =
    'fontSize' in element && element.fontSize
      ? getBoundedTypographyScale(
          element,
          Math.round(element.fontSize * heightMultiplier) / element.fontSize
        )
      : heightMultiplier;

  return syncTextListMarkerSize({
    ...element,
    ...((element.type === 'image' || element.type === 'shape') && {
      ...('backdropBlur' in element && {
        backdropBlur:
          Number.isFinite(element.backdropBlur) &&
          Number(element.backdropBlur) > 0
            ? Number(element.backdropBlur) * widthMultiplier
            : 0,
      }),
      ...('backdropEdgeSoftness' in element && {
        backdropEdgeSoftness:
          Number.isFinite(element.backdropEdgeSoftness) &&
          Number(element.backdropEdgeSoftness) > 0
            ? Number(element.backdropEdgeSoftness) * widthMultiplier
            : 0,
      }),
    }),
    width: Math.round(element.width * widthMultiplier),
    height: Math.round(element.height * heightMultiplier),
    x: Math.round(element.x * widthMultiplier),
    y: Math.round(element.y * heightMultiplier),
    ...('fontSize' in element && {
      fontSize: element.fontSize * typographyMultiplier,
      content: scaleInlineFontSizes(element.content, typographyMultiplier),
    }),
    ...('textBoxAttachment' in element &&
      element.textBoxAttachment && {
        textBoxAttachment: {
          ...element.textBoxAttachment,
          gap: Math.round(element.textBoxAttachment.gap * heightMultiplier),
          crossAxisOffset: Math.round(
            element.textBoxAttachment.crossAxisOffset * widthMultiplier
          ),
        },
      }),
  });
}
